const gradeRanks = new Map([
  ['A+', 10],
  ['A', 20],
  ['A-', 30],
  ['B+', 40],
  ['B', 50],
  ['B-', 60],
  ['C+', 70],
  ['C', 80],
  ['C-', 90]
]);

export function normalizeGrade(grade) {
  return String(grade || '').trim().toUpperCase();
}

export function getGradeRank(grade) {
  return gradeRanks.get(normalizeGrade(grade)) || 999;
}

export function getGradeTone(grade) {
  const normalized = normalizeGrade(grade);
  if (normalized.startsWith('A')) return 'a';
  if (normalized.startsWith('B')) return 'b';
  if (normalized.startsWith('C')) return 'c';
  return 'pending';
}

export function filterRevenueCompanies(companies = [], query = '') {
  const needle = query.trim().toLowerCase();
  if (!needle) return companies;

  return companies.filter((company) => [
    company.businessName,
    company.website,
    company.industry,
    company.city,
    company.email,
    company.phone,
    company.status,
    company.prospectGrade
  ].some((value) => String(value || '').toLowerCase().includes(needle)));
}

export function filterRevenueCompaniesByPipeline(companies = [], quickFilter = 'all', statusFilter = 'all') {
  return companies.filter((company) => {
    const status = String(company.status || 'prospect');
    const matchesStatus = statusFilter === 'all' || status === statusFilter;
    if (!matchesStatus) return false;

    if (quickFilter === 'active') return !['won', 'lost'].includes(status);
    if (quickFilter === 'won_lost') return ['won', 'lost'].includes(status);
    return true;
  });
}

export function filterRevenueCompaniesByQueue(companies = [], queueId = 'best_fit') {
  return companies.filter((company) => {
    const flags = getRevenueCompanyFlags(company);

    if (queueId === 'needs_audit') return flags.active && !flags.hasAudit;
    if (queueId === 'ready_outreach') return flags.active && flags.hasAudit && flags.hasContact && !flags.meeting;
    if (queueId === 'follow_up') return flags.active && ['contacted', 'audit_sent', 'proposal_sent'].includes(flags.status);
    if (queueId === 'meetings') return flags.status === 'meeting_scheduled';
    if (queueId === 'won_lost') return ['won', 'lost'].includes(flags.status);
    return flags.active;
  });
}

export function getCompactRevenueSummary({ metrics = {}, pipelineSummary = {} } = {}) {
  return [
    ['Prospects', metrics.prospectsCreated || pipelineSummary.total || 0],
    ['Audits', metrics.auditsGenerated || pipelineSummary.audited || 0],
    ['Meetings', metrics.meetingsBooked || 0],
    ['Revenue', formatMoney(metrics.revenueCents || 0)],
    ['A-grade', pipelineSummary.highGrade || 0],
    ['Needs audit', pipelineSummary.needsAudit || 0]
  ].map(([label, value]) => ({ label, value }));
}

export function getRevenueCommandSummary({ metrics = {}, companies = [] } = {}) {
  const counts = companies.reduce((summary, company) => {
    const flags = getRevenueCompanyFlags(company);
    return {
      highGrade: summary.highGrade + (flags.highGrade ? 1 : 0),
      needsAudit: summary.needsAudit + (flags.active && !flags.hasAudit ? 1 : 0),
      readyToContact: summary.readyToContact + (flags.active && flags.hasAudit && flags.hasContact && !flags.meeting ? 1 : 0),
      followUp: summary.followUp + (flags.active && ['contacted', 'audit_sent', 'proposal_sent'].includes(flags.status) ? 1 : 0),
      meetings: summary.meetings + (flags.meeting ? 1 : 0),
      won: summary.won + (flags.status === 'won' ? 1 : 0)
    };
  }, { highGrade: 0, needsAudit: 0, readyToContact: 0, followUp: 0, meetings: 0, won: 0 });

  return [
    ['A-grade', counts.highGrade],
    ['Needs audit', counts.needsAudit],
    ['Ready to contact', counts.readyToContact],
    ['Follow-up', counts.followUp],
    ['Meetings', metrics.meetingsBooked || counts.meetings],
    ['Won', metrics.dealsWon || counts.won]
  ].map(([label, value]) => ({ label, value }));
}

export function getRevenueNextAction(company = {}) {
  const flags = getRevenueCompanyFlags(company);

  if (flags.status === 'won') {
    return { label: 'Protect win', tone: 'success', description: 'Keep the relationship warm and watch for expansion opportunities.' };
  }
  if (flags.status === 'lost') {
    return { label: 'Review loss', tone: 'muted', description: 'Capture why the opportunity stalled before starting a new touchpoint.' };
  }
  if (flags.meeting) {
    return { label: 'Prep meeting', tone: 'primary', description: 'Review the audit, likely objections, and the strongest opening offer.' };
  }
  if (!flags.hasWebsite) {
    return { label: 'Add website', tone: 'warning', description: 'Record the website before running an audit.' };
  }
  if (!flags.hasAudit) {
    return { label: 'Generate audit', tone: 'primary', description: 'Create a scorecard, quick wins, automation ideas, and outreach assets.' };
  }
  if (!flags.hasContact) {
    return { label: 'Add contact', tone: 'warning', description: 'Add an email or phone number before preparing outreach.' };
  }
  if (flags.status === 'proposal_sent') {
    return { label: 'Follow proposal', tone: 'primary', description: 'Follow up with the most relevant business outcome from the proposal.' };
  }
  if (['contacted', 'audit_sent'].includes(flags.status)) {
    return { label: 'Send follow-up', tone: 'primary', description: 'Use the audit angle to keep the conversation moving toward a meeting.' };
  }

  return { label: 'Review outreach', tone: 'primary', description: 'Confirm the audit angle and prepare the first approved touchpoint.' };
}

export function sortRevenueCompanies(companies = [], sortKey = 'grade_desc') {
  const sorted = [...companies];
  sorted.sort((left, right) => {
    switch (sortKey) {
      case 'grade_asc':
        return compareGrade(left, right, 'asc') || compareName(left, right);
      case 'score_desc':
        return compareScore(left, right, 'desc') || compareGrade(left, right, 'desc') || compareName(left, right);
      case 'score_asc':
        return compareScore(left, right, 'asc') || compareGrade(left, right, 'desc') || compareName(left, right);
      case 'name_asc':
        return compareName(left, right);
      case 'city_asc':
        return compareText(left.city, right.city) || compareName(left, right);
      case 'status_asc':
        return compareText(left.status, right.status) || compareGrade(left, right, 'desc') || compareName(left, right);
      case 'grade_desc':
      default:
        return compareGrade(left, right, 'desc') || compareScore(left, right, 'asc') || compareName(left, right);
    }
  });
  return sorted;
}

export function summarizeRevenueCompanies(companies = []) {
  return companies.reduce((summary, company) => {
    const grade = normalizeGrade(company.prospectGrade);
    const score = Number(company.overallScore);
    const hasAudit = Boolean(company.latestAuditId || (Number.isFinite(score) && score > 0));

    return {
      total: summary.total + 1,
      highGrade: summary.highGrade + (grade.startsWith('A') ? 1 : 0),
      audited: summary.audited + (hasAudit ? 1 : 0),
      needsAudit: summary.needsAudit + (hasAudit ? 0 : 1)
    };
  }, { total: 0, highGrade: 0, audited: 0, needsAudit: 0 });
}

export function getRevenueCompanyFlags(company = {}) {
  const status = String(company.status || 'prospect');
  const score = Number(company.overallScore);
  const hasAudit = Boolean(company.latestAuditId || (Number.isFinite(score) && score > 0));
  const grade = normalizeGrade(company.prospectGrade);

  return {
    status,
    active: !['won', 'lost'].includes(status),
    meeting: status === 'meeting_scheduled',
    highGrade: grade.startsWith('A'),
    hasAudit,
    hasContact: Boolean(String(company.email || '').trim() || String(company.phone || '').trim()),
    hasWebsite: Boolean(String(company.website || '').trim())
  };
}

function compareGrade(left, right, direction) {
  const result = getGradeRank(left.prospectGrade) - getGradeRank(right.prospectGrade);
  return direction === 'asc' ? -result : result;
}

function compareScore(left, right, direction) {
  const leftScore = getComparableScore(left.overallScore, direction);
  const rightScore = getComparableScore(right.overallScore, direction);
  return direction === 'asc' ? leftScore - rightScore : rightScore - leftScore;
}

function getComparableScore(score, direction) {
  const value = Number(score);
  if (Number.isFinite(value) && value > 0) return value;
  return direction === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
}

function compareName(left, right) {
  return compareText(left.businessName, right.businessName);
}

function compareText(left, right) {
  return String(left || '').localeCompare(String(right || ''), undefined, { sensitivity: 'base' });
}

function formatMoney(cents, currency = 'usd') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: String(currency || 'usd').toUpperCase()
  }).format(Number(cents || 0) / 100);
}
